/**
 * PO Lines Tab Component
 *
 * Refactored to use modular sub-components for improved maintainability.
 * All state management is handled by the usePOLinesState hook.
 */
import { useEffect, useState } from 'react';
import { Loader2 } from 'lucide-react';
import { Card, CardContent } from '../../ui/card';
import ImportPromiseDatesModal, { type ImportPromiseDatesMethod } from './ImportPromiseDatesModal';
import OrderAckImportModal from '../OrderAckImportModal';
import POLineDetailModal from '../POLineDetailModal';
import OTIFBulkOverrideModal from '../OTIFBulkOverrideModal';
import { AcceptDateModal, DeclineDateModal } from '../AcknowledgmentWorkflowModals';
import { useFormatters } from '../../../contexts/PreferencesContext';
import { SectionTourTrigger } from '../../tour/SectionTourTrigger';

// Import modular components
import {
  usePOLinesState,
  POLinesFiltersPanel,
  POLinesTable,
  PutOnHoldModal,
  ReleaseHoldModal,
  BulkRejectModal,
  BulkHoldModal,
  BulkProposeChangeModal,
  BulkAcceptConfirmDialog,
  BulkResultLogDialog,
  PurchaseOrderLine,
} from './po-lines';

interface POLinesTabProps {
  initialLineId?: number | null;
  initialLineTab?: string;  // CR-3929: Auto-open specific tab in line detail (e.g., 'queries')
}

export default function POLinesTab({ initialLineId, initialLineTab }: POLinesTabProps = {}) {
  const { formatDate } = useFormatters();

  const {
    // Data
    lines,
    loading,
    error,
    currentPage,
    totalPages,
    totalCount,
    needsActionCount,
    otifReviewCount,
    duePreviousWeeksCount,
    dueThisWeekCount,
    dueNextWeekCount,
    countsRefreshing,
    partSearch,
    handleQuickPartSearch,
    openCount,
    closedCount,
    onHoldCount,
    pageSize,
    servedPageSize,
    activeTab,
    showFilters,
    user,

    // Selection
    selectedLineId,
    selectedLineAction,
    selectedLineForHold,
    selectedLineForWorkflow,
    selectedLineIds,
    bulkAccepting,
    bulkRejecting,
    bulkHolding,
    bulkProposing,
    showBulkAcceptModal,
    showBulkRejectModal,
    showBulkHoldModal,
    showBulkProposeModal,
    pendingAcceptIds,
    pendingAcceptVerb,
    pendingRejectIds,
    pendingHoldIds,
    pendingProposeIds,
    showImportModal,
    // CR-6679: Order Acknowledgements import modal
    showAckImportModal,

    // Modals
    putOnHoldModalOpen,
    releaseHoldModalOpen,
    acceptModalOpen,
    declineModalOpen,
    holdReason,

    // Sort
    sortField,
    sortDirection,

    // Export
    exporting,

    // Filters
    filters,
    filterOptions,

    // Columns
    columns,
    showColumnDropdown,
    columnDropdownRef,

    // Setters
    setActiveTab,
    setShowFilters,
    setSelectedLineId,
    setSelectedLineAction,
    setSelectedLineForWorkflow,
    setSelectedLineIds,
    setPutOnHoldModalOpen,
    setReleaseHoldModalOpen,
    setAcceptModalOpen,
    setDeclineModalOpen,
    setHoldReason,
    holdNextAction,
    setHoldNextAction,
    placerSide,
    setShowColumnDropdown,

    // Handlers
    fetchLines,
    refreshAfterMutation,
    patchLine,
    handlePageChange,
    handlePageSizeChange,
    handleFilterChange,
    handleSearch,
    clearFilters,
    handleSort,
    toggleColumn,
    resetColumns,
    selectAllColumns,
    reorderColumns,
    handlePutOnHold,
    handleReleaseHold,
    submitPutOnHold,
    submitReleaseHold,
    handleToggleCritical,
    handleWorkflowAction,
    handleSelectAll,
    handleToggleSelect,
    handleBulkAccept,
    handleBulkReject,
    handleBulkPutOnHold,
    handleBulkProposeChange,
    requestBulkAccept,
    requestBulkReject,
    requestBulkHold,
    requestBulkPropose,
    setShowBulkAcceptModal,
    setShowBulkRejectModal,
    setShowBulkHoldModal,
    setShowBulkProposeModal,
    bulkResultLog,
    showBulkResultLog,
    // CR-7418 — bulk OTIF override on the review pill
    bulkOverridingOtif,
    showBulkOtifModal,
    setShowBulkOtifModal,
    bulkOtifAction,
    setBulkOtifAction,
    otifBulkProgress,
    otifRetryableIds,
    stopBulkOverrideOtif,
    handleBulkOverrideOtif,
    setShowBulkResultLog,
    setShowImportModal,
    // CR-6679: Order Acknowledgements import modal
    setShowAckImportModal,
    handleExport,
    getTemplateExportParams,
    shouldShowActions,
    shouldShowDecline,
    shouldShowProposeChange,
    getNextAction,
    getSelectableLines,
    canSelectForBulkAccept,
  } = usePOLinesState();

  // CR-4728: Track which tab to open in the PO Line detail modal
  // (e.g., 'change-requests' when clicking the Review Changes icon)
  const [selectedLineTab, setSelectedLineTab] = useState<string | undefined>(undefined);
  // CR-7778: which of the customer's two uploads the modal is for.
  const [importMethod, setImportMethod] = useState<ImportPromiseDatesMethod>('customer_updates');

  // CR-3929: Auto-open PO line detail from notification deep link.
  // Customer review 11 Sep (stream I, from C): the link's `lineTab` belongs to
  // THAT opening only. It now rides in selectedLineTab (cleared on close)
  // instead of being re-applied whenever this line id is opened, so opening
  // the same line again from the grid starts on Detail.
  useEffect(() => {
    if (initialLineId) {
      setSelectedLineId(initialLineId);
      setSelectedLineTab(initialLineTab);
    }
  }, [initialLineId, initialLineTab]); // eslint-disable-line react-hooks/exhaustive-deps -- setSelectedLineId is the hook's state setter (stable)

  // Loading state - only show full-page loader when loading AND no data exists yet
  // CR-5766: Match Quality.tsx behavior to prevent filter section from re-rendering
  // when clearing filters or refreshing while data is already displayed.
  // Customer review 11 Sep (stream I, from C): the loader and the error card
  // replace the LIST, not the whole tab. They used to be early returns, so
  // any list reload that started from an empty page unmounted the open PO
  // line modal with it; the modal re-mounted on its initial tab and undid the
  // tab the user had just clicked (a deep link's first seconds). The modals
  // below are independent of the list and now stay mounted through it.
  const listBlocker = loading && lines.length === 0 ? (
    <div className="flex items-center justify-center min-h-[400px]">
      <div className="text-center">
        <Loader2 className="h-8 w-8 animate-spin mx-auto text-primary" />
        <p className="mt-2 text-sm text-muted-foreground">Loading PO lines...</p>
      </div>
    </div>
  ) : error ? (
    <Card>
      <CardContent className="pt-6">
        <div className="bg-red-50 border border-red-200 rounded-lg p-4">
          <p className="text-red-800">Error: {error}</p>
          <button
            onClick={() => fetchLines()}
            className="mt-2 text-sm text-red-600 hover:text-red-800 underline"
          >
            Try again
          </button>
        </div>
      </CardContent>
    </Card>
  ) : null;

  // Handle workflow modal opening
  // CR-4647: Supplier accept skips the modal — accepts immediately.
  const handleSelectLineForWorkflow = async (line: PurchaseOrderLine, modal: 'accept' | 'decline') => {
    const isSupplierView = String(user?.platform) === 'SUPPLIER' || line.acknowledge_status === 'new';
    if (modal === 'accept' && isSupplierView) {
      try {
        await handleWorkflowAction({ action: 'accept', lineId: line.id });
      } catch {
        // error already toasted by handleWorkflowAction
      }
      return;
    }
    setSelectedLineForWorkflow(line);
    if (modal === 'accept') {
      setAcceptModalOpen(true);
    } else {
      setDeclineModalOpen(true);
    }
  };

  // Handle propose change action
  const handleProposeChange = (line: PurchaseOrderLine) => {
    setSelectedLineAction('propose_change');
    setSelectedLineId(line.id);
  };

  return (
    <div className="space-y-4">
      {/* The list, or what stands in for it while it loads / fails. */}
      {listBlocker ?? (
        <>
          {/* Section Tour Trigger */}
          <div className="flex justify-end">
            <SectionTourTrigger
              pageKey="purchasing"
              sectionKey="po_lines"
              variant="link"
              label="Tour this tab"
            />
          </div>

          {/* Filters Panel — always mounted; the panel decides what to show per
              portal mode (CR-6347: the customer header persists while sections
              collapse and owns Refresh/Export; supplier renders nothing when
              hidden). */}
          <POLinesFiltersPanel
            filters={filters}
            filterOptions={filterOptions}
            onFilterChange={handleFilterChange}
            onSearch={handleSearch}
            onClearFilters={clearFilters}
            showFilters={showFilters}
            onToggleFilters={() => setShowFilters((v) => !v)}
            onRefresh={handleSearch}
            onExport={handleExport}
            exporting={exporting}
          />

          {/* Lines Table */}
          <POLinesTable
            lines={lines}
            totalCount={totalCount}
            needsActionCount={needsActionCount}
            otifReviewCount={otifReviewCount}
            onOtifDone={refreshAfterMutation}
            onOpenBulkOtif={(action) => { setBulkOtifAction(action); setShowBulkOtifModal(true); }}
            duePreviousWeeksCount={duePreviousWeeksCount}
            dueThisWeekCount={dueThisWeekCount}
            dueNextWeekCount={dueNextWeekCount}
            countsRefreshing={countsRefreshing}
            partSearch={partSearch}
            onQuickPartSearch={handleQuickPartSearch}
            openCount={openCount}
            closedCount={closedCount}
            onHoldCount={onHoldCount}
            currentPage={currentPage}
            totalPages={totalPages}
            pageSize={pageSize}
            servedPageSize={servedPageSize}
            activeTab={activeTab}
            user={user}
            columns={columns}
            showColumnDropdown={showColumnDropdown}
            columnDropdownRef={columnDropdownRef}
            sortField={sortField}
            sortDirection={sortDirection}
            selectedLineIds={selectedLineIds}
            bulkAccepting={bulkAccepting}
            bulkRejecting={bulkRejecting}
            bulkProposing={bulkProposing}
            formatDate={formatDate}
            onSetActiveTab={setActiveTab}
            onPageChange={handlePageChange}
            onPageSizeChange={handlePageSizeChange}
            onSort={handleSort}
            onToggleColumn={toggleColumn}
            onResetColumns={resetColumns}
            onSelectAllColumns={selectAllColumns}
            onReorderColumns={reorderColumns}
            onSetShowColumnDropdown={setShowColumnDropdown}
            onClearFilters={clearFilters}
            onSelectLine={setSelectedLineId}
            onReviewChanges={(lineId: number) => { setSelectedLineId(lineId); setSelectedLineTab('change-requests'); }}
            onSelectLineForWorkflow={handleSelectLineForWorkflow}
            onProposeChange={handleProposeChange}
            onToggleCritical={handleToggleCritical}
            onPutOnHold={handlePutOnHold}
            onReleaseHold={handleReleaseHold}
            onSelectAll={handleSelectAll}
            onToggleSelect={handleToggleSelect}
            onBulkAccept={(ids, verb) => requestBulkAccept(ids, verb)}
            onBulkReject={(ids) => requestBulkReject(ids)}
            onBulkHold={(ids) => requestBulkHold(ids)}
            onBulkPropose={(ids) => requestBulkPropose(ids)}
            bulkHolding={bulkHolding}
            onImportPromiseDates={(method) => { setImportMethod(method ?? 'customer_updates'); setShowImportModal(true); }}
            onImportAcknowledgements={() => setShowAckImportModal(true)}
            onSetSelectedLineIds={setSelectedLineIds}
            shouldShowActions={shouldShowActions}
            shouldShowDecline={shouldShowDecline}
            shouldShowProposeChange={shouldShowProposeChange}
            getNextAction={getNextAction}
            getSelectableLines={getSelectableLines}
            canSelectForBulkAccept={canSelectForBulkAccept}
            filters={filters}
          />
        </>
      )}

      {/* PO Line Detail Modal */}
      {selectedLineId && (
        <POLineDetailModal
          key={selectedLineId}
          lineId={selectedLineId}
          isOpen={selectedLineId !== null}
          onClose={() => {
            setSelectedLineId(null);
            setSelectedLineAction(null);
            setSelectedLineTab(undefined);
          }}
          userType={user?.platform === 'SUPPLIER' ? 'supplier' : 'customer'}
          initialAction={selectedLineAction}
          initialTab={selectedLineTab}
          onUpdate={() => patchLine(selectedLineId)}
        />
      )}

      {/* Put On Hold Modal */}
      <PutOnHoldModal
        isOpen={putOnHoldModalOpen}
        line={selectedLineForHold}
        holdReason={holdReason}
        onHoldReasonChange={setHoldReason}
        holdNextAction={holdNextAction}
        onHoldNextActionChange={setHoldNextAction}
        placerSide={placerSide}
        onClose={() => setPutOnHoldModalOpen(false)}
        onSubmit={submitPutOnHold}
      />

      {/* Release Hold Modal */}
      <ReleaseHoldModal
        isOpen={releaseHoldModalOpen}
        line={selectedLineForHold}
        onClose={() => setReleaseHoldModalOpen(false)}
        onSubmit={submitReleaseHold}
      />

      {/* Bulk Accept / Acknowledge Confirmation Modal (CR-5972: verb depends
          on which status group triggered the bar — Accept for Changed lines,
          Acknowledge for New lines). The verb only affects the surface copy;
          the API call is the same for both. */}
      <BulkAcceptConfirmDialog
        open={showBulkAcceptModal}
        onOpenChange={setShowBulkAcceptModal}
        count={pendingAcceptIds?.size ?? selectedLineIds.size}
        verb={pendingAcceptVerb ?? 'accept'}
        accepting={bulkAccepting}
        onConfirm={handleBulkAccept}
      />

      {/* Bulk Reject Confirmation Modal with Reason Selection */}
      <BulkRejectModal
        open={showBulkRejectModal}
        onOpenChange={setShowBulkRejectModal}
        count={pendingRejectIds?.size ?? selectedLineIds.size}
        rejecting={bulkRejecting}
        onReject={(reason) => handleBulkReject(reason)}
      />

      {/* Bulk Put On Hold Modal */}
      <BulkHoldModal
        open={showBulkHoldModal}
        onOpenChange={setShowBulkHoldModal}
        count={pendingHoldIds?.size ?? selectedLineIds.size}
        holding={bulkHolding}
        onHold={(reason) => handleBulkPutOnHold(reason)}
      />

      {/* Bulk Propose Change Modal (CR-5972) */}
      <BulkProposeChangeModal
        open={showBulkProposeModal}
        onOpenChange={setShowBulkProposeModal}
        count={pendingProposeIds?.size ?? selectedLineIds.size}
        proposing={bulkProposing}
        onPropose={(data) => handleBulkProposeChange(data)}
      />

      {/* Upload supplier / customer updates modal (CR-7778) */}
      <ImportPromiseDatesModal
        isOpen={showImportModal}
        method={importMethod}
        onClose={() => setShowImportModal(false)}
        totalCount={totalCount}
        onImportComplete={() => {
          // After import, status changes (e.g. new → accepted). Clear filters
          // and switch to "all" tab so the user sees the updated lines.
          clearFilters();
          setActiveTab('all');
          refreshAfterMutation();
        }}
        exportParams={getTemplateExportParams()}
      />

      {/* CR-6679: Order Acknowledgements Import Modal */}
      <OrderAckImportModal
        isOpen={showAckImportModal}
        onClose={() => setShowAckImportModal(false)}
        totalCount={totalCount}
        onImportComplete={() => {
          // After import, acknowledgements applied. Clear filters and refresh.
          clearFilters();
          setActiveTab('all');
          refreshAfterMutation();
        }}
        exportParams={getTemplateExportParams()}
      />

      {/* CR-7418: bulk OTIF override — the same dialog the review queue used,
          so the reason codes and the wording the supplier receives are one
          thing and not two. The draft survives a part-failed run only while
          the selection is still exactly what that run left behind. */}
      <OTIFBulkOverrideModal
        action={bulkOtifAction}
        open={showBulkOtifModal}
        onOpenChange={setShowBulkOtifModal}
        count={selectedLineIds.size}
        saving={bulkOverridingOtif}
        progress={otifBulkProgress}
        onStop={stopBulkOverrideOtif}
        preserveDraft={selectedLineIds.size > 0
          && Array.from(selectedLineIds).every(id => otifRetryableIds.has(id))}
        onSubmit={handleBulkOverrideOtif}
      />

      {/* Bulk Operation Failure Log */}
      <BulkResultLogDialog
        open={showBulkResultLog}
        onOpenChange={setShowBulkResultLog}
        entries={bulkResultLog}
      />

      {/* Workflow Modals */}
      {selectedLineForWorkflow && (
        <>
          <AcceptDateModal
            isOpen={acceptModalOpen}
            onClose={() => {
              setAcceptModalOpen(false);
              setSelectedLineForWorkflow(null);
            }}
            lineId={selectedLineForWorkflow.id}
            lineNumber={selectedLineForWorkflow.line_number}
            partNumber={selectedLineForWorkflow.part_number}
            orderNumber={selectedLineForWorkflow.po_number}
            currentDeliveryDate={selectedLineForWorkflow.customer_required_date}
            onSubmit={handleWorkflowAction}
          />
          <DeclineDateModal
            isOpen={declineModalOpen}
            onClose={() => {
              setDeclineModalOpen(false);
              setSelectedLineForWorkflow(null);
            }}
            lineId={selectedLineForWorkflow.id}
            lineNumber={selectedLineForWorkflow.line_number}
            partNumber={selectedLineForWorkflow.part_number}
            currentDeliveryDate={selectedLineForWorkflow.customer_required_date}
            onSubmit={handleWorkflowAction}
          />
        </>
      )}
    </div>
  );
}

